import React from "react";
import {Tooltip, Typography} from "@mui/material";
import {PlatformTable} from "../PlatformTable";
import {withAppContext} from "../../util/withAppContext";
import withNavigate from "../../util/useNavigate";
import withBreakpoint from "../../util/withBreakpoint";
import {Grey7, Grey17} from "../../theme/styles";

class ObjectAuditHistoryTable extends PlatformTable {

    getCellRenderer(name) {
        if (name === "Change Summary Cell") {
            return this.renderChangeSummaryCell;
        }

        return super.getCellRenderer(name);
    }

    // The Change column holds the per-field before/after diff the backend recorded. It is a
    // StorageTypeJSON attribute, so it arrives BASE64-encoded rather than as an object -- decoding is
    // this renderer's job, and it has to survive both a raw-JSON value and an undecodable one rather
    // than throwing inside a grid cell.
    renderChangeSummaryCell = (___, params, __, _) => {
        const row = params?.row;
        const raw = row?.get ? row.get("Change") : row?.Change;

        // A cell renderer fills the row's full height, so its content sits at the TOP unless the
        // wrapper claims that height and centres in it -- the same treatment the object number and
        // datetime cells get. Every return below goes through this, including the dashes: a dash
        // sitting high while the row beside it is centred reads as a broken row rather than an empty
        // one.
        const cell = (content) => (
            <div style={{display: "flex", alignItems: "center", height: "100%", minWidth: 0}}>
                {content}
            </div>
        );

        if (!raw) {
            return cell(<Typography style={{fontSize: 13, color: Grey7}}>—</Typography>);
        }

        let changes = null;
        try {
            changes = typeof raw === "string" ? JSON.parse(atob(raw)) : raw;
        } catch (e) {
            try {
                changes = typeof raw === "string" ? JSON.parse(raw) : null;
            } catch (e2) {
                changes = null;
            }
        }

        if (!Array.isArray(changes) || changes.length === 0) {
            return cell(<Typography style={{fontSize: 13, color: Grey7}}>—</Typography>);
        }

        const fields = changes.map(c => c["Field Path"]).filter(Boolean);
        const summary = fields.join(", ");
        const detail = changes
            .map(c => `${c["Field Path"]}: ${c["Previous Value"] ?? "∅"} → ${c["Next Value"] ?? "∅"}`)
            .join("\n");

        return cell(
            <Tooltip title={<span style={{whiteSpace: "pre-line"}}>{detail}</span>} placement="left">
                <Typography style={{fontSize: 13, color: Grey17, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                    {summary || `${changes.length} change(s)`}
                </Typography>
            </Tooltip>
        );
    };
}

export default withNavigate(withAppContext(withBreakpoint(ObjectAuditHistoryTable)));
